import { flushSync } from 'react-dom'

import type { PhoneCountry } from '../../lib/phone/countries'
import { parseE164, buildE164, validatePhone } from '../../lib/phone/utils'

// Process autofill value
export function processAutofillValue(
  rawValue: string,
  selectedCountry: PhoneCountry,
  countries: PhoneCountry[],
  isProcessingAutofillRef: React.MutableRefObject<boolean>,
  setSelectedCountry: React.Dispatch<React.SetStateAction<PhoneCountry>>,
  setNationalNumber: React.Dispatch<React.SetStateAction<string>>,
  onChange?: (value: string | null, meta: { countryIso2: string; callingCode: string; national: string; isValid: boolean }) => void,
  inputRef?: React.MutableRefObject<HTMLInputElement | null>
): boolean {
  if (isProcessingAutofillRef.current) return false
  
  const digitsOnly = rawValue.replace(/\D/g, '')
  
  // If we have more digits than expected for national number, try to parse as full number
  if (digitsOnly.length > selectedCountry.nationalDigits) {
    let fullNumber = digitsOnly
    if (!fullNumber.startsWith('+')) {
      fullNumber = '+' + fullNumber
    }
    
    try {
      const parsed = parseE164(fullNumber, countries)
      if (parsed.country && parsed.national) {
        isProcessingAutofillRef.current = true
        
        // Update state immediately - this will trigger re-render with correct country
        flushSync(() => {
          setSelectedCountry(parsed.country!)
          setNationalNumber(parsed.national)
        })
        
        const e164 = buildE164(parsed.country, parsed.national)
        const isValid = e164 ? validatePhone(e164) : false
        
        if (onChange) {
          onChange(e164, {
            countryIso2: parsed.country.iso2,
            callingCode: parsed.country.callingCode,
            national: parsed.national,
            isValid,
          })
        }
        
        // Update mask instance immediately
        requestAnimationFrame(() => {
          if (inputRef && inputRef.current) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const maskInstance = (inputRef.current as any).mask
            if (maskInstance) {
              // Update mask pattern if country changed
              if (maskInstance.mask !== parsed.country.mask) {
                maskInstance.updateOptions({ mask: parsed.country.mask })
              }
              // Set the value
              maskInstance.value = parsed.national
              maskInstance.updateValue()
            }
          }
          isProcessingAutofillRef.current = false
        })
        
        return true // Successfully parsed
      }
    } catch {
      // Ignore parsing errors
    }
  }
  return false
}
